import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || '',
  process.env.REACT_APP_SUPABASE_ANON_KEY || ''
);

// Landing page for all OAuth and email redirects.
// Supabase JS client automatically exchanges the code/token in the URL.
// We wait for onAuthStateChange to fire then navigate accordingly.
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('Completing sign-in...');

  useEffect(() => {
    // Handle case where session was already established before this component mounted
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        navigate('/dashboard', { replace: true });
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // Route back to login with a flag so the reset form shows
          navigate('/login?mode=reset', { replace: true });
          return;
        }

        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user) {
          setStatus('Signed in. Setting up your account...');

          // Auto-provision participant record for first-time OAuth users
          const { data: existing } = await supabase
            .from('participants')
            .select('id, role')
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (!existing) {
            // Admin may have pre-created this participant by email before first sign-in.
            // If found by email, link the auth UUID to the existing record so their
            // assignments and results carry over. Otherwise create a fresh record.
            const { data: byEmail } = await supabase
              .from('participants')
              .select('id, role')
              .eq('email', session.user.email)
              .maybeSingle();

            if (byEmail) {
              await supabase
                .from('participants')
                .update({ user_id: session.user.id })
                .eq('id', byEmail.id);
            } else {
              const meta = session.user.user_metadata || {};
              await supabase.from('participants').insert({
                user_id: session.user.id,
                email: session.user.email,
                first_name: meta.full_name?.split(' ')[0] || meta.name?.split(' ')[0] || meta.given_name || '',
                last_name: meta.full_name?.split(' ').slice(1).join(' ') || meta.family_name || '',
                role: 'participant',
              });
            }
          }

          navigate('/dashboard', { replace: true });
          return;
        }

        if (event === 'SIGNED_OUT' || (!session && event !== 'INITIAL_SESSION')) {
          navigate('/login', { replace: true });
        }
      }
    );

    // Fallback — if no event fires in 10s, redirect to login
    const fallback = setTimeout(() => {
      navigate('/login', { replace: true });
    }, 10000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallback);
    };
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0f1923 0%, #1a2f3f 50%, #0f1923 100%)',
      color: '#fff',
      fontFamily: 'Manrope, Arial, sans-serif',
      gap: '20px',
    }}>
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="24" cy="24" r="24" fill="url(#cbGrad)" />
        <path d="M14 24 Q24 12 34 24 Q24 36 14 24Z" fill="white" fillOpacity="0.9" />
        <circle cx="24" cy="24" r="5" fill="white" />
        <defs>
          <linearGradient id="cbGrad" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
            <stop stopColor="#009999" /><stop offset="1" stopColor="#007777" />
          </linearGradient>
        </defs>
      </svg>
      <div style={{
        width: 36, height: 36,
        border: '3px solid rgba(0,153,153,0.3)',
        borderTopColor: '#009999',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', margin: 0 }}>{status}</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AuthCallback;
